import api from "../api/api";
import IAttachment, { IAttachmentResponseModel, mapAttachmentResponseModel } from "../interfaces/IAttachment";
import { GeoCoordinate } from "../interfaces/GeoCoordinate";
import IGeoCoordinate from "../interfaces/IGeoCoordinate";
import ILead, { ILeadCreateState, ILeadResponseModel } from "../interfaces/ILead";
import ILeadNote, { ILeadNoteResponseModel, mapLeadNoteResponseModel } from "../interfaces/ILeadNote";
import { parseDateTime } from "../utils/utils";

export default class LeadsApiService {
    public async getLeads(): Promise<ILead[]> {
        const response = await api.get<ILeadResponseModel[]>("/lead/get");
        return this.mapResponseModelsToLeads(response.data);
    }

    public async getLocationGrade(coordinate: IGeoCoordinate): Promise<string | null> {
        const response = await api.get<string | null>(`/lead/location-grade?latitude=${coordinate.latitude}&longitude=${coordinate.longitude}`);
        return response.data || null;
    }

    public async createLead(leadState: ILeadCreateState, centerPoint: IGeoCoordinate, parcelId: string, pipedriveCompanyId: number | null): Promise<{ id: number, city: string, state: string, zip: string, saleDate: Date | null, salePrice: number | null, locationGrade: string | null, modifiedDate: Date, modifiedByUser: string }> {
        const response = await api.post("/lead/create", {
            status: leadState.status,
            address: leadState.address,
            ownerName: leadState.ownerName,
            ownershipType: leadState.ownershipType,
            zoning: leadState.zoning,
            landSize: leadState.landSize,
            buildingSize: leadState.buildingSize,
            lotCoverage: leadState.lotCoverage,
            totalAssessment: leadState.totalAssessment,
            ownerAddress: leadState.ownerAddress,
            contactName: leadState.contactName,
            email: leadState.email,
            pipedriveCompanyId: pipedriveCompanyId,
            phoneNumber: leadState.phoneNumber,
            labelId: leadState.labelId,
            latitude: centerPoint.latitude,
            longitude: centerPoint.longitude,
            parcelId: parcelId,
        });

        return {
            id: response.data.id,
            city: response.data.city,
            state: response.data.state,
            zip: response.data.zip,
            saleDate: response.data.saleDate && new Date(response.data.saleDate),
            salePrice: response.data.salePrice,
            locationGrade: response.data.locationGrade,
            modifiedDate: parseDateTime(response.data.modifiedDate),
            modifiedByUser: response.data.modifiedByUser,
        };
    }

    public async updateLead(lead: ILead): Promise<{ modifiedDate: Date, modifiedByUser: string }> {
        const response = await api.post("/lead/update", {
            leadId: lead.id,
            status: lead.status,
            address: lead.address,
            zoning: lead.zoning,
            landSize: lead.landSize,
            usableArea: lead.usableArea,
            buildingSize: lead.buildingSize,
            lotCoverage: lead.lotCoverage,
            totalAssessment: lead.totalAssessment,
            ownerName: lead.ownerName,
            ownershipType: lead.ownershipType,
            ownerAddress: lead.ownerAddress,
            saleDate: lead.saleDate,
            salePrice: lead.salePrice,
            pipedriveCompanyId: lead.pipedriveCompanyId,
            contactName: lead.contactName,
            email: lead.email,
            phoneNumber: lead.phoneNumber,
            labelId: lead.labelId,
            activityId: lead.activityId,
        });

        return {
            modifiedDate: parseDateTime(response.data.modifiedDate)!,
            modifiedByUser: response.data.modifiedByUser,
        };
    }

    public async deleteLead(id: number): Promise<void> {
        await api.delete(`/lead/delete?leadId=${id}`);
    }

    public async createLeadNote(leadId: number, text: string): Promise<ILeadNote> {
        const response = await api.post<ILeadNoteResponseModel>("/lead/createNote", {
            leadId: leadId,
            text: text,
        });

        return mapLeadNoteResponseModel(response.data, leadId);
    }

    public async deleteLeadNote(noteId: number): Promise<void> {
        await api.delete(`/lead/deleteNote?noteId=${noteId}`);
    }

    public async getLeadAttachments(leadId: number): Promise<IAttachment[]> {
        const response = await api.get<IAttachmentResponseModel[]>(`/lead/attachments?id=${leadId}`);
        return response.data.map(mapAttachmentResponseModel);
    }

    public async uploadLeadAttachment(leadId: number, file: File): Promise<IAttachment> {
        const formData = new FormData();
        formData.append("file", file);

        const response = await api.post<IAttachmentResponseModel>(`/lead/uploadAttachment?id=${leadId}`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
        });

        return mapAttachmentResponseModel(response.data);
    }

    public async deleteLeadAttachment(leadId: number, key: string): Promise<void> {
        await api.delete(`/lead/deleteAttachment?id=${leadId}&key=${encodeURIComponent(key)}`);
    }

    private mapResponseModelsToLeads(leads: ILeadResponseModel[]): ILead[] {
        return leads.map(l => ({
            id: l.id,
            centerPoint: new GeoCoordinate(l.latitude, l.longitude),
            parcelId: l.parcelId,
            address: l.address,
            city: l.city,
            state: l.state,
            zip: l.zip,
            zoning: l.zoning,
            landSize: l.landSize,
            buildingSize: l.buildingSize,
            lotCoverage: l.lotCoverage,
            totalAssessment: l.totalAssessment,
            ownerName: l.ownerName,
            ownershipType: l.ownershipType,
            ownerAddress: l.ownerAddress,
            contactName: l.contactName,
            email: l.email,
            pipedriveCompanyId: l.pipedriveCompanyId,
            phoneNumber: l.phoneNumber,
            saleDate: l.saleDate ? new Date(l.saleDate) : null,
            salePrice: l.salePrice,
            status: l.status,
            labelId: l.labelId,
            locationGrade: l.locationGrade,
            propertyGrade: l.propertyGrade,
            usableArea: l.usableArea,
            modifiedByUser: l.modifiedByUser,
            modifiedDate: parseDateTime(l.modifiedDate),
            activityId: l.activityId,
            notes: l.notes.map(n => mapLeadNoteResponseModel(n, l.id)),
            hasExcelAttachments: l.hasExcelAttachments,
        }));
    }
}
